// src/features/live-board/components/LiveBoardPage.tsx
//
// «Reception board» (w7.u3, CF S6): one tablet-sized screen, no session
// timeout, four columns - Pendientes, En el kiosco, Necesitan a alguien,
// Esperando habitacion - plus the device strip and "today so far". Polls
// GET /api/v2/ui/board every 15 s (useLiveBoard), never a manual refresh
// button - CF S6's own "Reception... a board that needs no refresh".
//
// Two of the plan's own requirements are not buildable against this branch
// of the v2 read API, stated here rather than silently dropped (full detail
// in the unit's evidence file):
//  - "masked names": no board.* row carries a guest name, masked or
//    otherwise (journey_id/device_id/step_kind only) - rows are shown
//    by device + step instead.
//  - "acknowledge": V2Incident.AcknowledgedAt exists in the domain/DB but
//    no endpoint in any read-API branch lets the UI set it - incidents are
//    shown read-only (open/acknowledged status), no button that would
//    silently do nothing.
//  - "hotel threshold setting" (D35): V2PropertySettings.BoardThresholdsJson
//    exists in the domain/DB but is not exposed by any admin/settings
//    endpoint either - the highlight threshold below is a personal UI
//    preference (useViewSwitch, per logged-in user), not the hotel-wide,
//    all-receptionists-see-the-same-thing setting D35 describes.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import {
  HourglassEmptyRounded as PendingIcon,
  DesktopWindowsRounded as AtDeviceIcon,
  NotificationImportantRounded as NeedsHelpIcon,
  MeetingRoomRounded as RoomIcon,
  WarningAmberRounded as IncidentIcon,
  ErrorOutlineRounded as ErrorsIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useLiveBoard } from '@/hooks/useLiveBoard';
import { useViewSwitch } from '@/providers/preferencesProvider';
import { formatElapsedSeconds } from '@/lib/analyticsV2Format';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { BoardCountTile } from './BoardCountTile';
import { BoardRowColumn } from './BoardRowColumn';
import { DeviceStrip } from './DeviceStrip';
import { TodaySoFarStrip } from './TodaySoFarStrip';
import { incidentCauseLabel, termLabel } from '@/lib/v2Vocabulary';
import type { BoardRow, MetricResultDto } from '@/types/analyticsV2';

const DASH_PLACEHOLDER = '—';

/** Caption parts joined with the house separator, skipping the empty ones. */
const joinParts = (...parts: Array<string | null | undefined | false>): string => parts.filter(Boolean).join(' · ');

const findMetric = (data: MetricResultDto[] | null, id: string): MetricResultDto | undefined =>
  data?.find((m) => m.id === id);

const DEFAULT_THRESHOLD_MINUTES = '10';

export const LiveBoardPage: React.FC = () => {
  const translate = useTranslate();
  const property = useV2PropertySelection('liveBoard');

  const { propertyId } = property;
  const [thresholdMinutesText, setThresholdMinutesText] = useViewSwitch(
    'liveBoard',
    'thresholdMinutes',
    DEFAULT_THRESHOLD_MINUTES
  );
  const thresholdSeconds = (Number(thresholdMinutesText) || 0) * 60;

  const { data, loading, error, consecutiveFailures, newNeedsHelpIds, lastUpdatedAt, refresh } = useLiveBoard(propertyId);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, []);
  const secondsSincePoll = lastUpdatedAt ? Math.max(0, (now - lastUpdatedAt) / 1000) : 0;

  useEffect(() => {
    if (newNeedsHelpIds.length === 0) return;
    try {
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContextCtor();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch {
      // No Web Audio in this environment, or blocked by the autoplay policy
      // before any user gesture - the row itself still appears; sound is a
      // reinforcement, not the only signal.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNeedsHelpIds.join(',')]);

  const pending = findMetric(data, 'board.pending');
  const atDevice = findMetric(data, 'board.at_device');
  const needsHelp = findMetric(data, 'board.needs_help');
  const roomNotReady = findMetric(data, 'board.room_not_ready');
  const deviceStrip = findMetric(data, 'board.device_strip');

  // Journey and incident rows carry the device id only; its name comes from
  // the device strip. The id is the fallback for a device the strip lacks.
  const deviceLabels = useMemo(() => {
    const map = new Map<string, string>();
    const rows = deviceStrip && Array.isArray(deviceStrip.value) ? (deviceStrip.value as BoardRow[]) : [];
    rows.forEach((row) => {
      if (typeof row.device_id === 'string' && typeof row.label === 'string' && row.label) map.set(row.device_id, row.label);
    });
    return map;
  }, [deviceStrip]);
  const deviceName = (row: BoardRow): string => {
    const id = typeof row.device_id === 'string' ? row.device_id : null;
    return id ? deviceLabels.get(id) ?? id : translate('resources.liveBoard.unknownDevice');
  };
  // A guest card names the reservation, so two guests at one device never
  // read as the same card; the device moves to the caption.
  const reservationOf = (row: BoardRow): string | null =>
    typeof row.pms_reservation_id === 'string' && row.pms_reservation_id ? row.pms_reservation_id : null;
  const guestTitle = (row: BoardRow): string => {
    const reservation = reservationOf(row);
    return reservation ? translate('resources.liveBoard.reservation', { id: reservation }) : deviceName(row);
  };
  const todaySoFar = findMetric(data, 'board.today_so_far');
  const incidentsOpen = findMetric(data, 'board.incidents_open');
  const errorsNow = findMetric(data, 'board.errors_now');

  const highlightedAtDeviceKeys = useMemo(() => {
    if (!atDevice || atDevice.absent || !Array.isArray(atDevice.value) || thresholdSeconds <= 0) return new Set<string>();
    const keys = new Set<string>();
    (atDevice.value as BoardRow[]).forEach((row) => {
      const baseElapsed = typeof row.elapsed_seconds === 'number' ? row.elapsed_seconds : 0;
      const elapsedNow = baseElapsed + secondsSincePoll;
      if (elapsedNow >= thresholdSeconds) keys.add((row.journey_id as string) ?? JSON.stringify(row));
    });
    return keys;
  }, [atDevice, thresholdSeconds, secondsSincePoll]);

  return (
    <Box>
      <SectionHeader manualScreen="live-board"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            {/* The explanation is a tooltip, not helper text: four lines under the
                field made it taller than the selector beside it, and the centred
                row pushed its label out of the top of the header. */}
            <Tooltip title={translate('resources.liveBoard.threshold.helper')}>
              <TextField
                size="small"
                type="number"
                sx={{ width: 190 }}
                label={translate('resources.liveBoard.threshold.label')}
                value={thresholdMinutesText}
                onChange={(e) => setThresholdMinutesText(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment>, inputProps: { min: 0 } }}
              />
            </Tooltip>
          </Box>
        }
        subtitle={translate('resources.liveBoard.subtitle')}
        helpConfig={{
          titleKey: 'resources.liveBoard.help.title',
          descriptionKey: 'resources.liveBoard.help.description',
          featureKeys: [
            'resources.liveBoard.help.features.columns',
            'resources.liveBoard.help.features.deviceStrip',
            'resources.liveBoard.help.features.sound',
          ],
          tipKeys: ['resources.liveBoard.help.tips.threshold'],
        }}
        onRefresh={refresh}
        refreshing={loading}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && consecutiveFailures >= 2 && <FetchErrorBanner onRetry={refresh} />}

          <Box sx={{ mb: 2.5 }}>
            <TodaySoFarStrip metric={todaySoFar} loading={loading && !data} />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <DeviceStrip metric={deviceStrip} loading={loading && !data} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
            <BoardCountTile
              title={translate('resources.liveBoard.columns.pending')}
              icon={<PendingIcon />}
              metric={pending}
              loading={loading && !data}
            />
            <BoardRowColumn
              title={translate('resources.liveBoard.columns.atDevice')}
              icon={<AtDeviceIcon />}
              metric={atDevice}
              loading={loading && !data}
              emptyLabel={translate('resources.liveBoard.columns.atDeviceEmpty')}
              rowKey={(row, i) => (row.journey_id as string) ?? `${i}`}
              highlightedKeys={highlightedAtDeviceKeys}
              renderRow={(row) => {
                const baseElapsed = typeof row.elapsed_seconds === 'number' ? row.elapsed_seconds : 0;
                const stepKind = row.step_kind as string | undefined;
                return (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {guestTitle(row)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {joinParts(
                        reservationOf(row) && deviceName(row),
                        stepKind ? termLabel(stepKind, translate) : DASH_PLACEHOLDER,
                        formatElapsedSeconds(baseElapsed + secondsSincePoll),
                      )}
                    </Typography>
                  </Box>
                );
              }}
            />
            <BoardRowColumn
              title={translate('resources.liveBoard.columns.needsHelp')}
              icon={<NeedsHelpIcon />}
              metric={needsHelp}
              loading={loading && !data}
              emptyLabel={translate('resources.liveBoard.columns.needsHelpEmpty')}
              rowKey={(row, i) => (row.journey_id as string) ?? `${i}`}
              badgeCount={
                needsHelp && !needsHelp.absent && Array.isArray(needsHelp.value) ? (needsHelp.value as BoardRow[]).length : undefined
              }
              renderRow={(row) => {
                const signal = row.signal as string | undefined;
                return (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {guestTitle(row)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {joinParts(
                        signal ? translate(`resources.liveBoard.signals.${signal}`, { _: termLabel(signal, translate) }) : DASH_PLACEHOLDER,
                        typeof row.component === 'string' && termLabel(row.component, translate),
                        reservationOf(row) && deviceName(row),
                      )}
                    </Typography>
                  </Box>
                );
              }}
            />
            <BoardCountTile
              title={translate('resources.liveBoard.columns.roomNotReady')}
              icon={<RoomIcon />}
              metric={roomNotReady}
              loading={loading && !data}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <BoardRowColumn
              title={translate('resources.liveBoard.incidents.title')}
              icon={<IncidentIcon />}
              metric={incidentsOpen}
              loading={loading && !data}
              emptyLabel={translate('resources.liveBoard.incidents.empty')}
              rowKey={(row, i) => `${row.device_id as string}-${row.opened_at as string}-${i}`}
              renderRow={(row) => (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {joinParts(deviceName(row), typeof row.cause === 'string' ? incidentCauseLabel(row.cause, translate) : DASH_PLACEHOLDER)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.acknowledged_at
                      ? translate('resources.liveBoard.incidents.acknowledged')
                      : translate('resources.liveBoard.incidents.open')}
                  </Typography>
                </Box>
              )}
            />
            <BoardRowColumn
              title={translate('resources.liveBoard.errorsNow.title')}
              icon={<ErrorsIcon />}
              metric={errorsNow}
              loading={loading && !data}
              emptyLabel={translate('resources.liveBoard.errorsNow.empty')}
              rowKey={(row, i) => `${row.journey_id as string}-${i}`}
              renderRow={(row) => (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {typeof row.component === 'string' ? termLabel(row.component, translate) : DASH_PLACEHOLDER}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {joinParts(
                      typeof row.code === 'string' ? termLabel(row.code, translate) : DASH_PLACEHOLDER,
                      typeof row.step_kind === 'string' && termLabel(row.step_kind, translate),
                    )}
                  </Typography>
                </Box>
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default LiveBoardPage;
